import React, { useState, useEffect } from 'react';
import './PricingForm.css';
import { FaSpinner, FaCheckCircle, FaTimes, FaPaperPlane, FaUser, FaEnvelope, FaMapMarkerAlt, FaPhone, FaUserTie, FaCrown } from 'react-icons/fa';

// API Configuration
const API_BASE_URL = 'https://tstock.trillionstechsolutions.com';

const PricingForm = ({ isOpen, onClose, selectedPlan, pricingData }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    emailAddress: '',
    address: '',
    contactNumber: '',
    panNumber: '',
    agentName: '',
    utrId: '',
    transactionId: '',
    plan: selectedPlan || 'Free Plan',
    planAmount: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ loading: false, error: null, success: false });

  // Update plan amount and clear UTR ID and Transaction ID when plan changes
  useEffect(() => {
    if (pricingData && pricingData.length > 0) {
      const selectedPlanData = pricingData.find(plan => plan.title === formData.plan);
      const planAmount = selectedPlanData ? selectedPlanData.price.replace('/month', '') : '';
      
      setFormData(prev => ({
        ...prev,
        planAmount: planAmount,
        utrId: formData.plan === 'Free Plan' ? '' : prev.utrId,
        transactionId: formData.plan === 'Free Plan' ? '' : prev.transactionId
      }));
    }
  }, [formData.plan, pricingData]);

  // Update form data when selectedPlan prop changes
  useEffect(() => {
    if (selectedPlan && selectedPlan !== formData.plan) {
      setFormData(prev => ({
        ...prev,
        plan: selectedPlan
      }));
    }
  }, [selectedPlan, formData.plan]);

  // Initialize plan amount when component mounts
  useEffect(() => {
    if (pricingData && pricingData.length > 0 && !formData.planAmount) {
      const selectedPlanData = pricingData.find(plan => plan.title === formData.plan);
      const planAmount = selectedPlanData ? selectedPlanData.price.replace('/month', '') : '';
      
      setFormData(prev => ({
        ...prev,
        planAmount: planAmount
      }));
    }
  }, [pricingData, formData.plan, formData.planAmount]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ loading: true, error: null, success: false });

    try {
      // Ensure planAmount is set before submission
      const selectedPlanData = pricingData.find(plan => plan.title === formData.plan);
      const planAmount = selectedPlanData ? selectedPlanData.price.replace('/month', '') : formData.planAmount;
      
      const submissionData = {
        ...formData,
        planAmount: planAmount,
        submissionDate: new Date().toISOString(),
        submissionDateFormatted: new Date().toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      };
      
      console.log('Form submitted:', JSON.stringify(submissionData, null, 2));
      console.log('Plan Amount:', submissionData.planAmount);
      
      // Submit form data to the API endpoint
      const response = await fetch(`${API_BASE_URL}/sales/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('API Response:', result);
      
      setSubmitStatus({ loading: false, error: null, success: true });

      // Reset form after successful submission
      setFormData({
        fullName: '',
        emailAddress: '',
        address: '',
        contactNumber: '',
        panNumber: '',
        agentName: '',
        utrId: '',
        transactionId: '',
        plan: selectedPlan || 'Free Plan',
        planAmount: ''
      });

      // Close the form after 3 seconds on success
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        loading: false,
        error: error.message || 'Failed to submit form. Please try again.',
        success: false
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pricing-form-overlay">
      <div className="pricing-form-modal">
        <div className="pricing-form-header">
          <h2>Get Started with {selectedPlan || 'Your Plan'}</h2>
          <p>Please fill in your details to proceed with the selected plan</p>
          <button className="pricing-form-close-btn" onClick={onClose}>×</button>
        </div>

        {submitStatus.error && (
          <div className="status-message error-message">
            <FaTimes className="status-icon" />
            {submitStatus.error}
          </div>
        )}

        {submitStatus.success && (
          <div className="status-message success-message">
            <FaCheckCircle className="status-icon" />
            Thank you for your interest! We'll contact you soon to proceed with your plan.
          </div>
        )}

        <form onSubmit={handleSubmit} className="pricing-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fullName">
                <FaUser className="input-icon" />
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                placeholder="Enter your full name"
                disabled={submitStatus.loading || submitStatus.success}
              />
            </div>
            <div className="form-group">
              <label htmlFor="emailAddress">
                <FaEnvelope className="input-icon" />
                Email Address
              </label>
              <input
                type="email"
                id="emailAddress"
                name="emailAddress"
                value={formData.emailAddress}
                onChange={handleChange}
                required
                placeholder="Enter your email"
                disabled={submitStatus.loading || submitStatus.success}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contactNumber">
                <FaPhone className="input-icon" />
                Contact Number
              </label>
              <input
                type="tel"
                id="contactNumber"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                required
                placeholder="Enter your contact number"
                disabled={submitStatus.loading || submitStatus.success}
              />
            </div>
            <div className="form-group">
              <label htmlFor="panNumber">
                <FaUser className="input-icon" />
                PAN Number
              </label>
              <input
                type="text"
                id="panNumber"
                name="panNumber"
                value={formData.panNumber}
                onChange={handleChange}
                required
                placeholder="Enter your PAN number"
                maxLength="10"
                pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                title="Please enter a valid PAN number (e.g., ABCDE1234F)"
                disabled={submitStatus.loading || submitStatus.success}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="agentName">
                <FaUserTie className="input-icon" />
                Agent Name (Optional)
              </label>
              <input
                type="text"
                id="agentName"
                name="agentName"
                value={formData.agentName}
                onChange={handleChange}
                placeholder="Enter agent name if any"
                disabled={submitStatus.loading || submitStatus.success}
              />
            </div>
            <div className="form-group">
              <label htmlFor="utrId">
                <FaUser className="input-icon" />
                UTR ID
              </label>
              <input
                type="text"
                id="utrId"
                name="utrId"
                value={formData.utrId}
                onChange={handleChange}
                placeholder="Enter your UTR ID"
                disabled={submitStatus.loading || submitStatus.success || formData.plan === 'Free Plan'}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="transactionId">
                <FaUser className="input-icon" />
                Transaction ID
              </label>
              <input
                type="text"
                id="transactionId"
                name="transactionId"
                value={formData.transactionId}
                onChange={handleChange}
                placeholder="Enter your Transaction ID"
                disabled={submitStatus.loading || submitStatus.success || formData.plan === 'Free Plan'}
              />
            </div>
            <div className="form-group">
              {/* Empty div for spacing in the grid */}
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="address">
              <FaMapMarkerAlt className="input-icon" />
              Address
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              placeholder="Enter your complete address"
              rows="3"
              disabled={submitStatus.loading || submitStatus.success}
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="plan">
              <FaCrown className="input-icon" />
              Selected Plan
            </label>
            <select
              id="plan"
              name="plan"
              value={formData.plan}
              onChange={handleChange}
              disabled={submitStatus.loading || submitStatus.success}
            >
              {pricingData.map((plan, index) => (
                <option key={index} value={plan.title}>
                  {plan.title} - {plan.price.replace('/month', '')}
                </option>
              ))}
            </select>
          </div>

          <button 
            type="submit" 
            className="pricing-form-submit-btn"
            disabled={submitStatus.loading || submitStatus.success}
          >
            {submitStatus.loading ? (
              <>
                <FaSpinner className="spinner" />
                Submitting...
              </>
            ) : submitStatus.success ? (
              <>
                <FaCheckCircle />
                Submitted Successfully
              </>
            ) : (
              <>
                <FaPaperPlane />
                Submit Application
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PricingForm;
